import type { MoneyValue } from '../api/types';
import type { AppLocale } from '../i18n/i18n';
import type { IranCurrencyDisplayMode } from './calendar';
import { normalizeLocalizedNumberInput } from './calendar';
import { formatMoney } from './formatters';

export interface IranCurrencyDisplayPolicy {
  mode: IranCurrencyDisplayMode;
  /** Tenant-owned denomination factor. A missing or invalid factor always falls back to source values. */
  rialsPerToman: number | null | undefined;
}

interface DecimalParts {
  coefficient: bigint;
  scale: number;
}

const MAX_DECIMAL_SCALE = 100;
const NON_TERMINATING_DIVISION_SCALE = 12;

function powerOfTen(exponent: number): bigint {
  return 10n ** BigInt(exponent);
}

function parseDecimal(value: string): DecimalParts | null {
  const normalized = normalizeLocalizedNumberInput(value).trim();
  const match = /^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/u.exec(normalized);
  if (match === null) return null;

  const fraction = match[3] ?? '';
  const exponent = Number.parseInt(match[4] ?? '0', 10);
  if (!Number.isInteger(exponent) || Math.abs(exponent) > MAX_DECIMAL_SCALE) return null;

  let coefficient = BigInt(`${match[2]}${fraction}`);
  if (match[1] === '-') coefficient = -coefficient;

  let scale = fraction.length - exponent;
  if (scale < 0) {
    coefficient *= powerOfTen(-scale);
    scale = 0;
  }
  if (scale > MAX_DECIMAL_SCALE) return null;
  return { coefficient, scale };
}

function decimalText(coefficient: bigint, scale: number): string {
  const negative = coefficient < 0n;
  let digits = (negative ? -coefficient : coefficient).toString();

  if (scale > 0) {
    digits = digits.padStart(scale + 1, '0');
    const split = digits.length - scale;
    digits = `${digits.slice(0, split)}.${digits.slice(split)}`.replace(/\.?0+$/u, '');
  }

  if (digits === '') digits = '0';
  return negative && digits !== '0' ? `-${digits}` : digits;
}

function greatestCommonDivisor(left: bigint, right: bigint): bigint {
  let a = left < 0n ? -left : left;
  let b = right < 0n ? -right : right;
  while (b !== 0n) [a, b] = [b, a % b];
  return a;
}

function multiplyDecimal(left: string, right: string): string | null {
  const leftParts = parseDecimal(left);
  const rightParts = parseDecimal(right);
  if (leftParts === null || rightParts === null) return null;
  return decimalText(
    leftParts.coefficient * rightParts.coefficient,
    leftParts.scale + rightParts.scale,
  );
}

function divideDecimal(left: string, right: string): string | null {
  const leftParts = parseDecimal(left);
  const rightParts = parseDecimal(right);
  if (leftParts === null || rightParts === null || rightParts.coefficient === 0n) return null;

  let numerator = leftParts.coefficient * powerOfTen(rightParts.scale);
  let denominator = rightParts.coefficient * powerOfTen(leftParts.scale);
  const negative = (numerator < 0n) !== (denominator < 0n);
  if (numerator < 0n) numerator = -numerator;
  if (denominator < 0n) denominator = -denominator;

  const divisor = greatestCommonDivisor(numerator, denominator);
  let reducedDenominator = denominator / divisor;
  let powersOfTwo = 0;
  let powersOfFive = 0;
  while (reducedDenominator % 2n === 0n) {
    reducedDenominator /= 2n;
    powersOfTwo += 1;
  }
  while (reducedDenominator % 5n === 0n) {
    reducedDenominator /= 5n;
    powersOfFive += 1;
  }

  const scale = reducedDenominator === 1n
    ? Math.max(powersOfTwo, powersOfFive)
    : NON_TERMINATING_DIVISION_SCALE;
  const scaledNumerator = numerator * powerOfTen(scale);
  let quotient = scaledNumerator / denominator;
  const remainder = scaledNumerator % denominator;
  if (remainder * 2n >= denominator) quotient += 1n;
  return decimalText(negative ? -quotient : quotient, scale);
}

/**
 * Builds display-only denomination values. The input is never mutated and no returned value is suitable
 * for posting, settlement, export, or replacing original-currency report columns.
 */
export function iranCurrencyDisplayValues(
  value: MoneyValue,
  policy: IranCurrencyDisplayPolicy | null | undefined,
): readonly MoneyValue[] {
  const sourceCode = value.currency.trim().toUpperCase();
  if ((sourceCode !== 'IRR' && sourceCode !== 'IRT') || policy === null || policy === undefined) {
    return [value];
  }

  const factor = policy.rialsPerToman;
  if (factor === null || factor === undefined || !Number.isFinite(factor) || factor <= 0) return [value];
  const factorText = String(factor);
  const rialAmount = sourceCode === 'IRR' ? value.amount : multiplyDecimal(value.amount, factorText);
  const tomanAmount = sourceCode === 'IRT' ? value.amount : divideDecimal(value.amount, factorText);
  if (rialAmount === null || tomanAmount === null) return [value];

  const rial = { amount: rialAmount, currency: 'IRR' };
  const toman = { amount: tomanAmount, currency: 'IRT' };
  if (policy.mode === 'rial') return [rial];
  if (policy.mode === 'toman') return [toman];
  return [rial, toman];
}

export function formatMoneyForDisplay(
  value: MoneyValue,
  locale: AppLocale,
  policy: IranCurrencyDisplayPolicy | null | undefined,
  compact = false,
): string {
  return iranCurrencyDisplayValues(value, policy)
    .map((displayValue) => formatMoney(displayValue, locale, compact))
    .join(' / ');
}
